/**
 * Get off the course.
 *
 * This is the only banner in the product that is allowed to be loud, and
 * it is loud on purpose. Everything else here follows the house rule that
 * the software states and does not shout — a by-law notice gets a gold
 * rule, a refusal gets a quiet red box. Lightning is the exception. A
 * member on the seventh green has a phone in their pocket and about four
 * minutes, and a banner that reads like a cookie notice is a banner they
 * will scroll past.
 *
 * Three things it deliberately does NOT do:
 *
 *   It does not clear itself. `minutes_remaining` counts down to the
 *   club's own rule and then sits at nought, still red, until a person
 *   sounds the all-clear. A timer reopening a course would be the
 *   software making the one decision it has no business making, and the
 *   countdown reaching zero is exactly when somebody is most tempted to
 *   let it.
 *
 *   It does not decide for itself what "clear" means. `course_is_clear`
 *   comes from the API so that a banner and a tee sheet cannot disagree
 *   about whether the course is shut.
 *
 *   It does not poll fast. Thirty seconds, because the alert reaches a
 *   member's phone by notification and this is the page they open after
 *   it — not the thing that has to be first.
 */

import { useEffect, useState } from 'react';
import { api } from '../lib/api.ts';

export interface CourseAlertRow {
  alert_id: string;
  course_id: string | null;
  course_name: string | null;
  kind: string;
  severity: string;
  source: string;
  detected_at: string;
  distance_km: string | number | null;
  message: string | null;
  cleared_at: string | null;
  cleared_note: string | null;
  raised_by_name: string | null;
  raised_by_device: string | null;
  cleared_by_name: string | null;
  is_open: boolean;
  may_resume_at: string | null;
  minutes_remaining: number;
}

export interface AlertsPayload {
  alerts: CourseAlertRow[];
  course_is_clear: boolean;
}

const HEADLINE: Record<string, string> = {
  lightning: 'Lightning — leave the course',
  severe_weather: 'Severe weather — leave the course',
  evacuation: 'Evacuate the course',
  air_quality: 'Air quality — play is suspended',
  other: 'Play is suspended',
};

const WATCH: Record<string, string> = {
  lightning: 'Lightning nearby — be ready to come in',
  severe_weather: 'Weather watch — be ready to come in',
  evacuation: 'Stand by to leave the course',
  air_quality: 'Air quality warning',
  other: 'Watch',
};

/** Miles, because the people reading this are standing in Illinois. */
function distance(km: string | number | null): string | null {
  if (km === null || km === undefined) return null;
  const n = Number(km);
  if (!Number.isFinite(n)) return null;
  const miles = n / 1.609344;
  return miles < 1 ? 'less than a mile away' : `about ${Math.round(miles)} miles away`;
}

function since(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins === 1) return 'a minute ago';
  if (mins < 60) return `${mins} minutes ago`;
  const h = Math.round(mins / 60);
  return h === 1 ? 'an hour ago' : `${h} hours ago`;
}

/**
 * `key` exists because polling alone is not enough on the screen that
 * raises and clears them. A professional who sounds the all-clear and
 * then watches the red banner sit there for another twenty seconds has
 * every reason to think the button did not work — and on a lightning
 * banner specifically, "did that work?" is the worst possible doubt to
 * leave somebody with. Bump the key after a mutation and the banner
 * agrees with the button immediately. Caught in a browser; the API tests
 * had no opinion about it.
 */
export function useCourseAlerts(pollMs = 30_000, key = 0) {
  const [data, setData] = useState<AlertsPayload | null>(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api<AlertsPayload>('/v1/golf/alerts');
        if (alive) setData(r);
      } catch {
        // A banner that cannot load must not blank the page under it.
        // The failure is visible where alerts are managed; here it is a
        // banner that does not appear, which is what happens on a normal
        // day anyway.
      }
    };
    void load();
    const t = setInterval(load, pollMs);
    return () => { alive = false; clearInterval(t); };
  }, [pollMs, key]);

  return data;
}

export function CourseAlertBanner({ data }: { data: AlertsPayload | null }) {
  const open = (data?.alerts ?? []).filter((a) => a.is_open);
  if (open.length === 0) return null;

  return (
    <div className="alerts">
      {open.map((a) => {
        const warning = a.severity === 'warning';
        const far = distance(a.distance_km);
        return (
          <div key={a.alert_id}
            className={warning ? 'course-alert' : 'course-alert course-alert--watch'}
            role="alert">
            <div className="course-alert__head">
              {warning ? (HEADLINE[a.kind] ?? HEADLINE.other) : (WATCH[a.kind] ?? WATCH.other)}
            </div>

            <div className="course-alert__body">
              {a.message ? <p>{a.message}</p> : null}
              <p className="course-alert__meta">
                {a.course_name ? `${a.course_name} · ` : 'Every course · '}
                {since(a.detected_at)}
                {far ? ` · ${far}` : ''}
                {a.raised_by_device
                  ? ` · reported by ${a.raised_by_device}`
                  : a.raised_by_name ? ` · called by ${a.raised_by_name}` : ''}
              </p>

              {/* The countdown, and immediately underneath it the sentence
                  that stops somebody treating the countdown as a promise. */}
              {warning && (
                <p className="course-alert__meta">
                  {a.minutes_remaining > 0
                    ? `Your club waits after the last strike — ${a.minutes_remaining} minute`
                      + `${a.minutes_remaining === 1 ? '' : 's'} to go.`
                    : 'The waiting period has elapsed.'}
                  {' '}Play resumes only when the shop sounds the all-clear.
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
